import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import api from '../../services/api';
import TaskCard from '../common/TaskCard';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const MyTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('assigned'); // assigned, in-progress, review-pending, completed
  const [cadenceFilter, setCadenceFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusCounts, setStatusCounts] = useState({
    assigned: 0,
    inProgress: 0,
    reviewPending: 0,
    completed: 0
  });

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when tab changes
  }, [activeTab, cadenceFilter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = {
        status: activeTab,
        limit: 100
      };

      if (cadenceFilter !== 'all') {
        params.cadence = cadenceFilter;
      }

      const response = await api.get('/employee/tasks', { params });
      setTasks(response.data.tasks || []);
      setStatusCounts({
        assigned: response.data.statusCounts?.assigned || 0,
        inProgress: response.data.statusCounts?.inProgress || 0,
        reviewPending: response.data.statusCounts?.reviewPending || 0,
        completed: response.data.statusCounts?.completed || 0
      });
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      task.title.toLowerCase().includes(query) ||
      task.description.toLowerCase().includes(query)
    );
  });

  const tabs = [
    { id: 'assigned', label: 'Assigned', count: statusCounts.assigned, color: 'blue' },
    { id: 'in-progress', label: 'In Progress', count: statusCounts.inProgress, color: 'blue' },
    { id: 'review-pending', label: 'Review Pending', count: statusCounts.reviewPending, color: 'amber' },
    { id: 'completed', label: 'Completed', count: statusCounts.completed, color: 'green' }
  ];

  const cadenceTabs = [
    { id: 'all', label: 'All Cadence' },
    { id: 'daily', label: 'Daily' },
    { id: 'weekly', label: 'Weekly' },
    { id: 'monthly', label: 'Monthly' },
    { id: 'yearly', label: 'Yearly' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Assigned Tasks</h1>
          <p className="text-gray-600 mt-1">View your daily, weekly and monthly assigned tasks</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`p-4 rounded-lg cursor-pointer transition-all ${
              activeTab === tab.id
                ? `bg-${tab.color}-50 border-2 border-${tab.color}-500`
                : 'bg-white border border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="text-center">
              <div className={`text-3xl font-bold mb-1 ${
                activeTab === tab.id ? `text-${tab.color}-600` : 'text-gray-900'
              }`}>
                {tab.count}
              </div>
              <div className="text-xs text-gray-600 font-medium">{tab.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow">
        {/* Cadence Tabs */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-wrap gap-2">
            {cadenceTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setCadenceFilter(tab.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  cadenceFilter === tab.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Task List */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredTasks.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredTasks.map((task) => (
                <TaskCard 
                  key={task._id} 
                  task={task} 
                  userRole="employee"
                  showAssignedDeadline
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <span className="text-3xl">📋</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {searchQuery ? 'No tasks found' : 'No tasks in this category'}
              </h3>
              <p className="text-gray-600">
                {searchQuery 
                  ? 'Try adjusting your search query' 
                  : activeTab === 'assigned' 
                    ? 'You have no new tasks assigned'
                    : `No ${tabs.find(t => t.id === activeTab)?.label.toLowerCase()} tasks${cadenceFilter !== 'all' ? ` for ${cadenceFilter}` : ''}`
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Task Status Guide:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Assigned:</strong> New tasks assigned to you</li>
          <li>• <strong>In Progress:</strong> Tasks you're currently working on</li>
          <li>• <strong>Review Pending:</strong> Tasks submitted for admin review</li>
          <li>• <strong>Completed:</strong> Tasks approved and closed by admin</li>
        </ul>
      </div>
    </div>
  );
};

export default MyTasks;
